import type { WidgetPosition } from "./config";

function hexToRgb(hex: string): [number, number, number] | null {
  const m = hex.replace("#", "").match(/^([0-9a-f]{6})$/i);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgba(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

const STYLES = `
:host {
  all: initial;
  position: fixed;
  z-index: 2147483000;
  font-family: system-ui, -apple-system, sans-serif;
}
:host(.bottom-right) { right: 20px; bottom: 20px; }
:host(.bottom-left) { left: 20px; bottom: 20px; }
:host(.top-right) { top: 20px; right: 20px; }
:host(.top-left) { top: 20px; left: 20px; }
:host(.right-center) { right: 0; top: 50%; transform: translateY(-50%); }
/* The attention message is a speech bubble anchored to whichever side of
   the widget bubble the dashboard configured (data-side), independent of
   widget_position (the bubble's own screen corner) -- the bubble is the
   only child of :host laid out in normal flow, so positioning relative to
   :host is positioning relative to the bubble itself, on every corner
   including right-center's tall tab. No auto-flipping: a tenant can pick
   a side that runs off-screen for a given corner, same as widget_position
   itself isn't guarded today. pointer-events: none so it's purely
   decorative; the bubble underneath is still what the visitor clicks. */
.attention {
  position: absolute; pointer-events: none;
  opacity: 0; transition: opacity .3s ease;
  background: #fff; color: #111827; font-size: 13px; line-height: 1.4;
  padding: 10px 14px; border-radius: 14px; box-shadow: 0 6px 20px rgba(0,0,0,.18);
}
.attention.visible { opacity: 1; }
.attention::after {
  content: ""; position: absolute; width: 10px; height: 10px;
  background: inherit; transform: rotate(45deg);
}
.attention[data-side="top"] {
  bottom: 100%; left: 50%; transform: translateX(-50%); margin-bottom: 12px;
}
.attention[data-side="top"]::after {
  top: 100%; left: 50%; margin-left: -5px; margin-top: -5px;
}
.attention[data-side="bottom"] {
  top: 100%; left: 50%; transform: translateX(-50%); margin-top: 12px;
}
.attention[data-side="bottom"]::after {
  bottom: 100%; left: 50%; margin-left: -5px; margin-bottom: -5px;
}
.attention[data-side="left"] {
  right: 100%; top: 50%; transform: translateY(-50%); margin-right: 12px;
}
.attention[data-side="left"]::after {
  left: 100%; top: 50%; margin-top: -5px; margin-left: -5px;
}
.attention[data-side="right"] {
  left: 100%; top: 50%; transform: translateY(-50%); margin-left: 12px;
}
.attention[data-side="right"]::after {
  right: 100%; top: 50%; margin-top: -5px; margin-right: -5px;
}
/* A top/bottom side centers the bubble horizontally over the host (left:
   50% + translateX(-50%) above) -- fine for a host placed away from a
   screen edge, but every real widget_position sits flush against one (all
   4 corners, plus right-center's edge tab), so centering routinely pushed
   roughly half the message -- including its text -- past the edge, where
   the viewport itself clips it (confirmed from a real screenshot: the
   message text was cut off mid-word). Align the message to whichever side
   of the host is away from the screen edge instead, so it only ever grows
   inward, never off-screen. */
:host(.bottom-right) .attention[data-side="top"],
:host(.bottom-right) .attention[data-side="bottom"],
:host(.top-right) .attention[data-side="top"],
:host(.top-right) .attention[data-side="bottom"],
:host(.right-center) .attention[data-side="top"],
:host(.right-center) .attention[data-side="bottom"] {
  left: auto; right: 0; transform: none;
}
:host(.bottom-right) .attention[data-side="top"]::after,
:host(.bottom-right) .attention[data-side="bottom"]::after,
:host(.top-right) .attention[data-side="top"]::after,
:host(.top-right) .attention[data-side="bottom"]::after,
:host(.right-center) .attention[data-side="top"]::after,
:host(.right-center) .attention[data-side="bottom"]::after {
  left: auto; right: 24px; margin-left: 0;
}
:host(.bottom-left) .attention[data-side="top"],
:host(.bottom-left) .attention[data-side="bottom"],
:host(.top-left) .attention[data-side="top"],
:host(.top-left) .attention[data-side="bottom"] {
  left: 0; right: auto; transform: none;
}
:host(.bottom-left) .attention[data-side="top"]::after,
:host(.bottom-left) .attention[data-side="bottom"]::after,
:host(.top-left) .attention[data-side="top"]::after,
:host(.top-left) .attention[data-side="bottom"]::after {
  right: auto; left: 24px; margin-left: 0;
}
.bubble {
  width: 56px; height: 56px; border-radius: 999px; border: none; cursor: pointer;
  background: var(--artis-bg, var(--artis-color, #111827)); color: #fff; display: flex;
  align-items: center; justify-content: center; box-shadow: 0 6px 20px rgba(0,0,0,.2);
  transition: transform .15s ease, box-shadow .15s ease;
  overflow: hidden;
}
.bubble:hover { transform: scale(1.06); }
.bubble.loading { opacity: .6; cursor: default; }
.bubble.loading:hover { transform: none; }
.bubble.connecting { animation: artis-pulse 1.1s ease-in-out infinite; }
/* A click while still loading (config not back yet) can't do anything,
   but it still needs to visibly acknowledge the click — otherwise an
   impatient visitor reads "nothing happened" as "my click didn't
   register" and clicks again (and again), instead of "it's loading,
   give it a second". */
.bubble.shake { animation: artis-shake .3s ease; }
@keyframes artis-shake {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(.92); }
}
/* right-center: a slim edge tab instead of a floating circle — a circle
   vertically centered on the page would sit on top of the site's own
   content; a tab flush against the edge reads as "part of the page chrome"
   and doesn't cover anything. Widened on hover and given a soft periodic
   glow (instead of the corner bubble's hover-scale) so it still calls
   attention without jumping into the page. */
:host(.right-center) .bubble {
  width: 48px; height: 120px; border-radius: 16px 0 0 16px;
  transition: width .2s ease, box-shadow .2s ease;
  animation: artis-tab-glow 3s ease-in-out infinite;
}
:host(.right-center) .bubble:hover { transform: none; width: 58px; }
:host(.right-center) .bubble.loading,
:host(.right-center) .bubble.connecting { animation: artis-pulse 1.1s ease-in-out infinite; }
@keyframes artis-tab-glow {
  0%, 100% { box-shadow: 0 6px 20px rgba(0,0,0,.2); }
  50% { box-shadow: 0 6px 20px rgba(0,0,0,.2), 0 0 0 8px rgba(0,0,0,.06); }
}
@keyframes artis-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: .55; }
}
.bubble svg { width: 24px; height: 24px; }
.bubble img {
  width: var(--artis-logo-scale, 100%);
  height: var(--artis-logo-scale, 100%);
  object-fit: contain;
  border-radius: inherit;
}
.bars rect {
  animation: artis-bounce 0.9s ease-in-out infinite;
  transform-origin: center;
  fill: currentColor;
}
.bars rect:nth-child(1) { animation-delay: 0s; }
.bars rect:nth-child(2) { animation-delay: 0.15s; }
.bars rect:nth-child(3) { animation-delay: 0.3s; }
@keyframes artis-bounce {
  0%, 100% { transform: scaleY(0.4); }
  50% { transform: scaleY(1); }
}
.dots circle {
  animation: artis-dot-bounce 1s ease-in-out infinite;
  fill: currentColor;
}
.dots circle:nth-child(1) { animation-delay: 0s; }
.dots circle:nth-child(2) { animation-delay: 0.15s; }
.dots circle:nth-child(3) { animation-delay: 0.3s; }
@keyframes artis-dot-bounce {
  0%, 80%, 100% { opacity: .3; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
}
/* The very first load (config not back from the dashboard yet) used to
   just be a dimmer, static icon — nothing on screen actually moved, so
   it read as "stuck" rather than "loading" to an impatient visitor.
   Two blinking "eyes" make it obviously alive while staying calmer than
   the in-call thinking/speaking indicators. */
.eyes circle {
  animation: artis-blink 2.4s ease-in-out infinite;
  transform-origin: center;
  fill: currentColor;
}
@keyframes artis-blink {
  0%, 92%, 100% { transform: scaleY(1); }
  96% { transform: scaleY(.1); }
}
.panel {
  position: absolute; width: 300px; border-radius: 16px;
  background: #fff; box-shadow: 0 10px 40px rgba(0,0,0,.2); overflow: hidden;
  display: flex; flex-direction: column;
  opacity: 0; transform: translateY(8px) scale(.98); pointer-events: none;
  transition: opacity .15s ease, transform .15s ease;
}
.panel.open { opacity: 1; transform: translateY(0) scale(1); pointer-events: auto; }
:host(.bottom-right) .panel, :host(.bottom-left) .panel { bottom: 68px; }
:host(.top-right) .panel, :host(.top-left) .panel { top: 68px; }
:host(.bottom-right) .panel, :host(.top-right) .panel { right: 0; }
:host(.bottom-left) .panel, :host(.top-left) .panel { left: 0; }
:host(.right-center) .panel {
  top: 50%; right: 62px; max-height: calc(100vh - 40px); overflow-y: auto;
  transform: translateY(calc(-50% + 8px)) scale(.98);
}
:host(.right-center) .panel.open { transform: translateY(-50%) scale(1); }
/* "button" mode has no panel to show an error in (removed entirely, since
   its own close control would be unreachable) — this toast is the only
   on-screen feedback a visitor gets for an error in that mode. Reuses the
   panel's anchor rules since it's the same "small popover near the bubble"
   shape. */
.toast {
  position: absolute; max-width: 240px; padding: 10px 14px; border-radius: 10px;
  background: #b91c1c; color: #fff; font-size: 12px; line-height: 1.4;
  box-shadow: 0 6px 20px rgba(0,0,0,.25);
  opacity: 0; transform: translateY(8px); pointer-events: none;
  transition: opacity .15s ease, transform .15s ease;
}
.toast.visible { opacity: 1; transform: translateY(0); }
:host(.bottom-right) .toast, :host(.bottom-left) .toast { bottom: 68px; }
:host(.top-right) .toast, :host(.top-left) .toast { top: 68px; }
:host(.bottom-right) .toast, :host(.top-right) .toast { right: 0; }
:host(.bottom-left) .toast, :host(.top-left) .toast { left: 0; }
:host(.right-center) .toast {
  top: 50%; right: 62px;
  transform: translateY(calc(-50% + 8px));
}
:host(.right-center) .toast.visible { transform: translateY(-50%); }
.panel-header {
  background: var(--artis-header-bg, var(--artis-color, #111827)); color: #fff; padding: 14px 16px;
  font-size: 14px; font-weight: 600; display: flex; justify-content: space-between; align-items: center;
}
.panel-header button { background: none; border: none; color: #fff; cursor: pointer; font-size: 16px; opacity: .8; }
.panel-body { padding: 16px; font-size: 13px; color: #374151; min-height: 60px; }
.status { color: #6b7280; margin-bottom: 8px; }
.error { color: #b91c1c; margin-top: 8px; }
.actions { display: flex; gap: 8px; }
button.primary {
  flex: 1; padding: 10px; border-radius: 10px; border: none; cursor: pointer;
  background: var(--artis-bg, var(--artis-color, #111827)); color: #fff; font-size: 13px; font-weight: 600;
}
button.primary:disabled { opacity: .5; cursor: default; }

:host(.glass) .bubble {
  background: var(--artis-glass-bg, rgba(255,255,255,.35));
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border: 1px solid rgba(255,255,255,.4);
  box-shadow: 0 8px 32px rgba(0,0,0,.18), inset 0 1px 1px rgba(255,255,255,.5);
}
:host(.glass) button.primary {
  background: var(--artis-glass-bg, rgba(255,255,255,.35));
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border: 1px solid rgba(255,255,255,.4);
}
:host(.glass) .panel {
  background: var(--artis-glass-bg, rgba(255,255,255,.55));
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid rgba(255,255,255,.4);
  box-shadow: 0 10px 40px rgba(0,0,0,.15);
}
:host(.glass) .panel-header {
  background: var(--artis-glass-header-bg, var(--artis-glass-bg, rgba(255,255,255,.35)));
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255,255,255,.3);
}
`;

const MIC_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>`;
const SPEAKING_ICON = `<svg class="bars" viewBox="0 0 24 24"><rect x="4" y="9" width="3" height="6" rx="1"/><rect x="10.5" y="6" width="3" height="12" rx="1"/><rect x="17" y="9" width="3" height="6" rx="1"/></svg>`;
const THINKING_ICON = `<svg class="dots" viewBox="0 0 24 24"><circle cx="6" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="18" cy="12" r="2"/></svg>`;
const LOADING_EYES_ICON = `<svg class="eyes" viewBox="0 0 24 24"><circle cx="8" cy="12" r="3"/><circle cx="16" cy="12" r="3"/></svg>`;
const CLOSE_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
const CHAT_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>`;
const HEADSET_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 14v-3a9 9 0 0 1 18 0v3"/><path d="M21 15v2a2 2 0 0 1-2 2h-1v-6h1a2 2 0 0 1 2 2z"/><path d="M3 15v2a2 2 0 0 0 2 2h1v-6H5a2 2 0 0 0-2 2z"/></svg>`;
const PHONE_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`;
const SPARKLE_ICON = `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6z"/><path d="M19 15l.9 2.6L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.4z"/></svg>`;

export const WIDGET_ICON_PRESETS = ["mic", "chat", "headset", "phone", "sparkle"] as const;
export type WidgetIcon = (typeof WIDGET_ICON_PRESETS)[number];

const ICONS: Record<WidgetIcon, string> = {
  mic: MIC_ICON,
  chat: CHAT_ICON,
  headset: HEADSET_ICON,
  phone: PHONE_ICON,
  sparkle: SPARKLE_ICON,
};

export type WidgetState = "idle" | "connecting" | "live" | "ended" | "error";

export type AttentionSide = "top" | "bottom" | "left" | "right";

export interface WidgetAppearance {
  title: string;
  textColor: string | null;
  color: string | null;
  color2: string | null;
  gradient: boolean;
  mode: "full" | "button";
  style: "solid" | "glass";
  icon: WidgetIcon;
  logoUrl: string | null;
  /** Percentage (0-100] of the bubble the logo image fills. Defaults to a
   * full 100% fill when omitted, matching the pre-existing behavior. */
  logoScale: number | null;
  position: WidgetPosition;
}

export class WidgetUI {
  readonly root: HTMLDivElement;
  private readonly shadow: ShadowRoot;
  private readonly panel: HTMLDivElement;
  private readonly title: HTMLSpanElement;
  private readonly statusEl: HTMLDivElement;
  private readonly errorEl: HTMLDivElement;
  private readonly warningEl: HTMLDivElement;
  private readonly actionButton: HTMLButtonElement;
  private readonly bubble: HTMLButtonElement;
  private readonly attention: HTMLDivElement;
  private readonly toast: HTMLDivElement;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  // The server only sends live_session_timer once, at connect — it never
  // re-sends as time passes. Without a local tick, the displayed "X:XX
  // left" just sat frozen at whatever value arrived first (confirmed root
  // cause of a real report). Ticking client-side avoids needing the server
  // to push an update every second over the websocket for something this
  // cheap to track locally.
  private countdownIntervalId: ReturnType<typeof setInterval> | null = null;
  private remainingSeconds = 0;
  private open = false;
  private speaking = false;
  private thinking = false;
  private mode: "full" | "button" = "full";
  private iconHtml: string = MIC_ICON;
  private logoUrl: string | null = null;
  // The dashboard's mode/appearance config arrives async (fetchWidgetConfig),
  // after the bubble is already clickable — a visitor fast enough to click
  // before it lands would open a plain default panel (or, worse, get the
  // panel yanked out from under them a moment later if the real mode turns
  // out to be "button"). Ignore clicks until applyAppearance has run once,
  // so the very first click already reflects the real config.
  private ready = false;

  onOpen: (() => void) | null = null;
  onEnd: (() => void) | null = null;

  constructor(position: WidgetPosition) {
    this.root = document.createElement("div");
    this.root.className = `root ${position}`;
    // Stable marker for anything that needs to reliably recognize "this
    // element is Artis's own widget, not part of the host page" from
    // outside the shadow root -- e.g. Autofix's hidden/covered-button
    // detector (ai-service) must never flag the widget itself as
    // something covering a real page element. A class name ("root")
    // isn't unique enough to trust for that.
    this.root.setAttribute("data-artis-widget-root", "true");

    this.shadow = this.root.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = STYLES;
    this.shadow.appendChild(style);

    this.bubble = document.createElement("button");
    this.bubble.className = "bubble loading";
    this.bubble.innerHTML = LOADING_EYES_ICON;
    this.bubble.setAttribute("aria-label", "Talk to us");
    this.bubble.addEventListener("animationend", (e) => {
      if (e.animationName === "artis-shake") this.bubble.classList.remove("shake");
    });
    this.bubble.onclick = () => {
      if (!this.ready) {
        // Can't do anything yet, but the click still needs to visibly
        // register — otherwise it reads as broken instead of loading, and
        // an impatient visitor just clicks again (see the .shake rule).
        this.bubble.classList.remove("shake");
        // Force a reflow so re-adding the class restarts the animation
        // even if a previous shake hasn't finished yet.
        void this.bubble.offsetWidth;
        this.bubble.classList.add("shake");
        return;
      }
      if (this.mode === "button") this.actionButton.click();
      else this.toggle();
    };

    this.panel = document.createElement("div");
    this.panel.className = "panel";

    const header = document.createElement("div");
    header.className = "panel-header";
    this.title = document.createElement("span");
    this.title.textContent = "Talk to us";
    const closeBtn = document.createElement("button");
    closeBtn.innerHTML = CLOSE_ICON;
    closeBtn.setAttribute("aria-label", "Close");
    closeBtn.onclick = () => this.toggle();
    header.append(this.title, closeBtn);

    const body = document.createElement("div");
    body.className = "panel-body";
    this.statusEl = document.createElement("div");
    this.statusEl.className = "status";
    this.errorEl = document.createElement("div");
    this.errorEl.className = "error";
    this.warningEl = document.createElement("div");
    this.warningEl.className = "error";

    const actions = document.createElement("div");
    actions.className = "actions";
    this.actionButton = document.createElement("button");
    this.actionButton.className = "primary";
    this.actionButton.textContent = "Start talking";
    this.actionButton.onclick = () => {
      if (this.actionButton.textContent === "End call") this.onEnd?.();
      else this.onOpen?.();
    };
    actions.appendChild(this.actionButton);

    body.append(this.statusEl, this.errorEl, this.warningEl, actions);
    this.panel.append(header, body);

    this.attention = document.createElement("div");
    this.attention.className = "attention";
    this.attention.dataset.side = "top";

    this.toast = document.createElement("div");
    this.toast.className = "toast";

    this.shadow.append(this.bubble, this.attention, this.panel, this.toast);
  }

  /** Call once, as soon as the dashboard config resolves -- sets which side
   * of the bubble the message renders on and how wide it is. Width is
   * clamped to a sane range so a stray dashboard value (0, negative, or
   * absurdly large) can't break the layout; everything else about the
   * config is trusted as-is, same as the rest of applyAppearance. */
  setAttentionAppearance(side: AttentionSide, widthPx: number): void {
    this.attention.dataset.side = side;
    const clamped = Math.min(400, Math.max(120, widthPx || 220));
    // Also bounded by the viewport itself -- a configured width that's fine
    // on desktop can still be wider than a narrow phone screen, which would
    // clip the same way the edge-overflow fix below addresses horizontally.
    this.attention.style.maxWidth = `min(${clamped}px, calc(100vw - 40px))`;
  }

  /** No-op if a call is already open/connecting -- an attention-grabbing
   * message next to a bubble the visitor already engaged with would just
   * be noise. Purely decorative (pointer-events: none in CSS); the bubble
   * underneath is still what actually starts a call. textContent (not
   * innerHTML) is deliberate: tenant-authored text, rendered as plain
   * text, never HTML. */
  showAttentionMessage(text: string): void {
    if (this.open) return;
    this.attention.textContent = text;
    this.attention.classList.add("visible");
  }

  hideAttentionMessage(): void {
    this.attention.classList.remove("visible");
  }

  applyAppearance(appearance: WidgetAppearance): void {
    this.title.textContent = appearance.title;
    this.title.style.color = appearance.textColor || "";
    this.bubble.setAttribute("aria-label", appearance.title);
    this.mode = appearance.mode;
    if (this.mode === "button") {
      // Button mode never opens the panel — the bubble itself starts/ends
      // the call, so the header's own close control would be unreachable.
      this.panel.remove();
    }

    if (appearance.color) {
      this.root.style.setProperty("--artis-color", appearance.color);
    }
    if (appearance.color2) {
      if (appearance.gradient) {
        this.root.style.setProperty(
          "--artis-bg",
          `linear-gradient(135deg, ${appearance.color}, ${appearance.color2})`,
        );
        this.root.style.setProperty("--artis-header-bg", appearance.color2);
      } else {
        this.root.style.setProperty("--artis-header-bg", appearance.color2);
      }
    }

    if (appearance.style === "glass") {
      this.root.classList.add("glass");
      const c1 = appearance.color ? rgba(appearance.color, 0.55) : "rgba(255,255,255,.4)";
      this.root.style.setProperty("--artis-glass-bg", c1);
      if (appearance.color2) {
        const c2 = rgba(appearance.color2, 0.55);
        this.root.style.setProperty(
          "--artis-glass-bg",
          appearance.gradient ? `linear-gradient(135deg, ${c1}, ${c2})` : c1,
        );
        this.root.style.setProperty("--artis-glass-header-bg", c2);
      }
    }

    this.root.classList.remove(
      "bottom-right",
      "bottom-left",
      "top-right",
      "top-left",
      "right-center",
    );
    this.root.classList.add(appearance.position);

    this.logoUrl = appearance.logoUrl;
    this.root.style.setProperty(
      "--artis-logo-scale",
      `${appearance.logoScale ?? 100}%`,
    );
    this.iconHtml = ICONS[appearance.icon] || MIC_ICON;
    this.bubble.classList.toggle("has-logo", !!this.logoUrl);

    this.setReady(true);
  }

  /** Call once the dashboard config has resolved (applyAppearance calls this
   * itself) — or directly, if the fetch failed and there's no config to
   * apply, so the widget still works with its built-in defaults instead of
   * staying dead forever. Either path needs the bubble to swap off the
   * loading icon once ready, so that swap lives here rather than in each
   * caller. */
  setReady(ready: boolean): void {
    this.ready = ready;
    this.bubble.classList.toggle("loading", !ready);
    if (ready && !this.speaking && !this.thinking) {
      this.renderRestingIcon();
    }
  }

  private toggle(): void {
    this.open = !this.open;
    this.panel.classList.toggle("open", this.open);
  }

  /** Only ever shown in "button" mode — see the error case in setState(). */
  private showToast(message: string): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toast.textContent = message;
    this.toast.classList.add("visible");
    this.toastTimer = setTimeout(() => {
      this.toast.classList.remove("visible");
    }, 6000);
  }

  setState(state: WidgetState, detail?: string): void {
    this.errorEl.textContent = "";
    this.warningEl.textContent = "";
    if (state !== "live") {
      this.setSpeaking(false);
      this.setThinking(false);
      this.stopCountdown();
    }
    this.bubble.classList.toggle("connecting", state === "connecting");
    // The blinking eyes are the actual "this is loading" signal — the
    // .connecting class's opacity pulse alone wasn't a clear enough cue on
    // its own (confirmed: visitors kept re-clicking during this exact
    // window, between clicking to start talking and the agent going
    // live). Swap back to the resting icon for every other state, unless
    // speaking/thinking already owns the bubble's content.
    if (state === "connecting") {
      this.bubble.innerHTML = LOADING_EYES_ICON;
    } else if (!this.speaking && !this.thinking) {
      this.renderRestingIcon();
    }
    switch (state) {
      case "idle":
        this.statusEl.textContent = "";
        this.actionButton.textContent = "Start talking";
        this.actionButton.disabled = false;
        break;
      case "connecting":
        this.statusEl.textContent = "Connecting...";
        this.actionButton.disabled = true;
        break;
      case "live":
        this.statusEl.textContent = detail || "Listening — speak now";
        this.actionButton.textContent = "End call";
        this.actionButton.disabled = false;
        break;
      case "ended":
        this.statusEl.textContent = "Call ended";
        this.actionButton.textContent = "Start talking";
        this.actionButton.disabled = false;
        break;
      case "error": {
        const message = detail || "Something went wrong. Please try again.";
        this.statusEl.textContent = "";
        this.errorEl.textContent = message;
        this.actionButton.textContent = "Start talking";
        this.actionButton.disabled = false;
        if (this.mode === "button") {
          // No panel in this mode (removed in applyAppearance) — the error
          // text set above has nowhere visible to render, so a visitor
          // clicking would otherwise see nothing happen at all.
          console.error("[Artis widget]", detail);
          this.showToast(message);
        }
        break;
      }
    }
  }

  /** Whichever graphic sits in the bubble when it's neither thinking nor
   * speaking — the custom logo if one is set, the icon preset otherwise. */
  private renderRestingIcon(): void {
    if (this.logoUrl) {
      const img = document.createElement("img");
      img.src = this.logoUrl;
      img.alt = "";
      this.bubble.replaceChildren(img);
    } else {
      this.bubble.innerHTML = this.iconHtml;
    }
  }

  setSpeaking(speaking: boolean): void {
    if (this.speaking === speaking) return;
    this.speaking = speaking;
    if (speaking) {
      // Audio is flowing now — no longer just "thinking".
      this.thinking = false;
      this.bubble.innerHTML = SPEAKING_ICON;
    } else if (!this.thinking) {
      this.renderRestingIcon();
    }
  }

  /** The gap between the visitor going quiet and the agent's first audio
   * chunk had no visual feedback at all — this fills it in, same "swap the
   * bubble's content" pattern as setSpeaking so it works identically
   * whether or not a custom logo is configured. */
  setThinking(thinking: boolean): void {
    if (this.thinking === thinking || this.speaking) return;
    this.thinking = thinking;
    if (thinking) {
      this.bubble.innerHTML = THINKING_ICON;
    } else {
      this.renderRestingIcon();
    }
  }

  setMicWarning(message: string | null): void {
    this.warningEl.textContent = message || "";
  }

  setTimer(remainingSeconds: number): void {
    this.remainingSeconds = Math.max(0, remainingSeconds);
    this.renderTimer();
    this.stopCountdown();
    if (this.remainingSeconds <= 0) return;
    this.countdownIntervalId = setInterval(() => {
      this.remainingSeconds = Math.max(0, this.remainingSeconds - 1);
      this.renderTimer();
      if (this.remainingSeconds <= 0) this.stopCountdown();
    }, 1000);
  }

  private renderTimer(): void {
    const m = Math.floor(this.remainingSeconds / 60);
    const s = String(this.remainingSeconds % 60).padStart(2, "0");
    this.statusEl.textContent = `Listening — ${m}:${s} left`;
  }

  private stopCountdown(): void {
    if (this.countdownIntervalId === null) return;
    clearInterval(this.countdownIntervalId);
    this.countdownIntervalId = null;
  }

  openPanel(): void {
    if (this.mode === "button") return;
    this.open = true;
    this.panel.classList.add("open");
  }
}
